import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { API_get_compliance_audit } from "../../../../constants";
import { getRightworkAPI } from "../../requests/utilities/requests";

// Audits need the whole pay period for weekly rules. Fetch each overlapping
// period once, then retain only findings attributed to the displayed week.
export async function fetchActualComplianceAudit(storeId, startDate, endDate, { isCancelled = () => false, onPeriod = () => {}, timeoutMs } = {}) {
  const groups = [];
  const skipped = new Map();
  let cursor = startDate;
  while (cursor <= endDate) {
    if (isCancelled()) throw new Error("Audit cancelled");
    const response = await getRightworkAPI(API_get_compliance_audit, {
      store_id: storeId, date: cursor,
    }, timeoutMs ? { timeout: timeoutMs } : undefined);
    if (isCancelled()) throw new Error("Audit cancelled");
    const audit = response.data?.redux?.payload;
    if (response.status !== 200 || !Array.isArray(audit?.groups) ||
        !/^\d{4}-\d{2}-\d{2}$/.test(audit.pay_period_end) ||
        !dayjs(audit.pay_period_end).isValid() || audit.pay_period_end < cursor) {
      throw new Error("Compliance audit unavailable");
    }
    const from = cursor;
    const through = audit.pay_period_end < endDate ? audit.pay_period_end : endDate;
    audit.groups.forEach((group) => {
      const violations = group.violations.filter((v) => v.date >= from && v.date <= through);
      if (!violations.length) return;
      // Keep different legal authorities separate across regulation changes.
      const existing = groups.find((g) => g.regulation_type === group.regulation_type &&
        g.legal_citation === group.legal_citation && g.legal_description === group.legal_description);
      if (existing) {
        existing.violations.push(...violations);
        existing.violation_count = existing.violations.length;
      } else {
        groups.push({ ...group, violations, violation_count: violations.length });
      }
    });
    (audit.skipped_regulation_types || []).forEach((rule) => skipped.set(rule.regulation_type, rule));
    onPeriod(through);
    cursor = dayjs(audit.pay_period_end).add(1, "day").format("YYYY-MM-DD");
  }
  groups.sort((a, b) => b.violation_count - a.violation_count);
  return {
    groups,
    total_violations: groups.reduce((sum, group) => sum + group.violation_count, 0),
    skipped_regulation_types: [...skipped.values()],
  };
}

export default function useActualComplianceAudit({ enabled, ready = true, storeId, startDate, refreshKey }) {
  const [state, setState] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const scope = `${storeId}-${startDate}`;
  useEffect(() => {
    let active = true;
    if (enabled && ready && storeId && startDate) {
      const endDate = dayjs(startDate).add(6, "day").format("YYYY-MM-DD");
      fetchActualComplianceAudit(storeId, startDate, endDate)
        .then((data) => { if (active) setState({ scope, refreshKey, attempt, data }); })
        .catch(() => { if (active) setState({ scope, refreshKey, attempt, error: true }); });
    }
    return () => { active = false; };
  }, [enabled, ready, storeId, startDate, scope, refreshKey, attempt]);
  // Keep this week's last result visible during a background update, but
  // never reuse another location/week's findings or hide a failed refresh.
  const current = enabled && state?.scope === scope ? state : null;
  const pending = !ready || !current || current.refreshKey !== refreshKey || current.attempt !== attempt;
  return {
    data: current?.data || null,
    loading: enabled && pending && !current?.data,
    refreshing: enabled && pending && !!current?.data,
    error: !pending && !!current?.error,
    retry: () => setAttempt((value) => value + 1),
  };
}
